import type { EngineScore } from "@onchainreps/chess";
import { ChevronLeft, ChevronRight, EndIcon, FlipIcon, StartIcon } from "./Icons";
import { Chessboard } from "react-chessboard";
import type { ChessboardOptions } from "react-chessboard";

interface BoardStageProps {
  fen: string;
  orientation: "white" | "black";
  onFlip: () => void;
  onFirst: () => void;
  onPrev: () => void;
  onNext: () => void;
  onLast: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  whiteWinPercent: number | null;
  score: EngineScore | null;
  sideToMove: "white" | "black";
  bestMove: string | null;
  lastMove: { from: string; to: string } | null;
}

export function BoardStage({
  fen,
  orientation,
  onFlip,
  onFirst,
  onPrev,
  onNext,
  onLast,
  canGoBack,
  canGoForward,
  whiteWinPercent,
  score,
  sideToMove,
  bestMove,
  lastMove,
}: BoardStageProps) {
  const whiteShare = whiteWinPercent === null ? 50 : whiteWinPercent;

  const options: ChessboardOptions = {
    position: fen,
    boardOrientation: orientation,
    allowDragging: false,
    showNotation: true,
    animationDurationInMs: 180,
    darkSquareStyle: { backgroundColor: "var(--board-dark)" },
    lightSquareStyle: { backgroundColor: "var(--board-light)" },
    boardStyle: { borderRadius: "6px", boxShadow: "none" },
    squareStyles: lastMove
      ? {
          [lastMove.from]: { backgroundColor: "var(--board-check)" },
          [lastMove.to]: { backgroundColor: "var(--board-highlight)" },
        }
      : undefined,
    arrows: bestMove
      ? [
          {
            startSquare: bestMove.slice(0, 2),
            endSquare: bestMove.slice(2, 4),
            color: "var(--board-arrow)",
          },
        ]
      : [],
  };

  return (
    <div className="board-shell">
      <div className="board-frame">
        <div className="evalbar" aria-hidden="true">
          <div className="evalbar__mid" />
          <div className="evalbar__white" style={{ height: `${whiteShare}%` }} />
        </div>
        <div className="board-frame__board">
          <Chessboard options={options} />
        </div>
      </div>
      <div className="board-controls">
        <div className="board-controls__group">
          <button className="iconbtn" onClick={onFirst} disabled={!canGoBack} aria-label="First move">
            <StartIcon />
          </button>
          <button className="iconbtn" onClick={onPrev} disabled={!canGoBack} aria-label="Previous move">
            <ChevronLeft />
          </button>
          <button className="iconbtn" onClick={onNext} disabled={!canGoForward} aria-label="Next move">
            <ChevronRight />
          </button>
          <button className="iconbtn" onClick={onLast} disabled={!canGoForward} aria-label="Last move">
            <EndIcon />
          </button>
        </div>
        <button className="iconbtn" onClick={onFlip} aria-label="Flip board">
          <FlipIcon />
        </button>
      </div>
    </div>
  );
}
